import { z } from 'zod';
import { operatorApiFetch } from './apiService';
import { apiSuccess, apiList } from '@services/apiSchemas';

// 對應 app/controllers/api/operator/v1/inventories_controller.rb
//
// index → format_inventory
// get_records → 自訂 block（含 merchant / shelf_inventories / pending_putaway_quantity）
// get_ids → 回傳 string[]
// export → { job_id }
// shelf_inventories (member) → render_array_list, 自訂 block

const InventoryProductSchema = z.object({
  id: z.string().nullable(),
  sku: z.string().nullable(),
  name: z.string().nullable(),
}).passthrough();

const InventoryTypeRefSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  code: z.string().nullable(),
}).passthrough();

export const InventorySelectSchema = z.object({
  id: z.string(),
  product: InventoryProductSchema,
  inventory_type: InventoryTypeRefSchema,
  quantity: z.number(),
  available_quantity: z.number(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.unknown(),
}).passthrough();

const ShelfInventoryEntrySchema = z.object({
  id: z.string(),
  shelf_id: z.string(),
  shelf_location: z.string().nullable(),
  shelf_type_name: z.string().nullable(),
  shelf_role: z.string().nullable(),
  quantity: z.number(),
}).passthrough();

export const InventoryTableRowSchema = z.object({
  id: z.string(),
  merchant_id: z.string().nullable(),
  merchant_name: z.string().nullable(),
  product_id: z.string().nullable(),
  product_sku: z.string().nullable(),
  product_name: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  status: z.string(),
  quantity: z.number(),
  available_quantity: z.number(),
  allocated_quantity: z.number(),
  held_quantity: z.number(),
  pending_putaway_quantity: z.number(),
  shelf_inventories: z.array(ShelfInventoryEntrySchema),
  created_at: z.string(),
  custom_attributes: z.unknown(),
}).passthrough();

const ShelfInventoryWithAvailableSchema = ShelfInventoryEntrySchema.extend({
  available_quantity: z.number(),
}).passthrough();

const JobIdSchema = z.object({ job_id: z.string() }).passthrough();

export type InventorySelectItem = z.infer<typeof InventorySelectSchema>;
export type InventoryTableRow = z.infer<typeof InventoryTableRowSchema>;
export type ShelfInventoryWithAvailable = z.infer<typeof ShelfInventoryWithAvailableSchema>;

interface InventoryListParams {
  warehouse_id?: string;
  product_id?: string;
  merchant_id?: string;
  available?: boolean | string;
  search?: string;
  since_id?: string;
  page_size?: number | string;
}

export const inventoryService = {
  // 庫存列表（Select 下拉用，cursor mode paging）
  list: async (params: InventoryListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.warehouse_id) queryParams.append('warehouse_id', String(params.warehouse_id));
    if (params.product_id) queryParams.append('product_id', String(params.product_id));
    if (params.merchant_id) queryParams.append('merchant_id', String(params.merchant_id));
    if (params.available) queryParams.append('available', 'true');
    if (params.search) queryParams.append('search', String(params.search));
    if (params.since_id) queryParams.append('since_id', String(params.since_id));
    if (params.page_size) queryParams.append('page_size', String(params.page_size));

    const response = await operatorApiFetch(`inventories?${queryParams.toString()}`);
    return apiList(InventorySelectSchema).parse(await response.json());
  },

  // 獲取庫存列表（表格）
  getRecords: async (params: Record<string, unknown>) => {
    const response = await operatorApiFetch('inventories/get_records', {
      method: 'POST',
      body: params
    });
    return apiList(InventoryTableRowSchema).parse(await response.json());
  },

  // 獲取單一庫存（routes only: [:index]，目前 BE 未開放，保留 pass-through）
  get: async (id: string) => {
    const response = await operatorApiFetch(`inventories/${id}`);
    return response.json();
  },

  // 獲取庫存異動（BE 走 inventory_movements 獨立 resource，保留 pass-through）
  getMovements: async (id: string, params: Record<string, unknown> = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => queryParams.append(key, String(params[key])));

    const response = await operatorApiFetch(`inventories/${id}/movements?${queryParams.toString()}`);
    return response.json();
  },

  // 獲取所有庫存 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const response = await operatorApiFetch('inventories/get_ids', {
      method: 'POST',
      body: params
    });
    return apiSuccess(z.array(z.string())).parse(await response.json());
  },

  // 獲取庫存儲位分佈
  getShelfInventories: async (id: string) => {
    const response = await operatorApiFetch(`inventories/${id}/shelf_inventories`);
    return apiList(ShelfInventoryWithAvailableSchema).parse(await response.json());
  },

  // 匯出庫存
  export: async (ids: string[], params: Record<string, unknown> = {}) => {
    const response = await operatorApiFetch('inventories/export', {
      method: 'POST',
      body: { ids, ...params }
    });
    return apiSuccess(JobIdSchema).parse(await response.json());
  },

};

export default inventoryService;
